/**
 * Sprint C6 — Pure tag-filter validation. Single source für Server
 * (assets.ts listQuerySchema / statsQuerySchema) und Client (LibraryPage
 * `tagKey`, Sidebar active-highlight). Keine Server-only-deps — Vitest
 * importiert direkt ohne Mock-Setup.
 */

/** Max Tags pro `?tag=`-Query — DoS-Defense. UI nutzt max 4 pro Kategorie. */
export const MAX_TAG_FILTER = 10;

/**
 * Parsed einen kommaseparierten `?tag=`-Query-Param zu einer normalisierten
 * Tag-Liste: trim + lowercase pro Slug, leere Slugs verworfen, dedupliziert
 * (first-occurrence-Order erhalten), gecappt auf MAX_TAG_FILTER.
 *
 * KEINE Zeichen-Validierung — `tags.name` ist im Schema ein freier `text`-Wert
 * (bestehende `tagsArraySchema` erzwingt nur trim/min/max, keine Slug-Regex).
 * Jeder nicht-leere getrimmte Token ist ein gültiger Tag-Kandidat; ein Token
 * der keinen Tag in der DB matched führt zu leerem Result, NICHT zu "kein
 * Filter".
 *
 * Returnt `[]` nur für strukturell leere Inputs (null / undefined / "" /
 * ",," / "   ") — der Caller interpretiert `[]` als "kein Filter".
 */
export function parseTagFilter(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const slug = part.trim().toLowerCase();
    if (!slug) continue;
    if (seen.has(slug)) continue;
    seen.add(slug);
    out.push(slug);
    if (out.length >= MAX_TAG_FILTER) break;
  }
  return out;
}

/** Max UUIDs pro Facetten-Filter-Param (wg/kategorie/produkt/verwendung) — DoS-/
 *  URL-Längen-Defense. UI nutzt heute Single-Select; OR-Liste ist Server-Vorrat. */
export const MAX_UUID_FILTER = 20;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Parsed einen kommaseparierten Facetten-Filter-Param (Taxonomie Phase 3) zu einer
 * normalisierten UUID-Liste: trim + lowercase pro Token, NUR gültige UUIDs (Regex),
 * invalide/leere Tokens verworfen, dedupliziert (first-occurrence-Order), gecappt
 * auf MAX_UUID_FILTER. Returnt `[]` für strukturell leere/komplett-invalide Inputs —
 * der Caller interpretiert `[]` als „kein Filter" (graceful, kein 400).
 */
export function parseUuidFilter(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const id = part.trim().toLowerCase();
    if (!id || !UUID_RE.test(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_UUID_FILTER) break;
  }
  return out;
}
